/**
 * @description Global hook to listen to user channel for unread messages count updates
 * @note Handles both global unread count tracking and automatic chat selection (if in ChatContext)
 * @note Use this at the app level (App.tsx) for global unread count tracking
 */

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/features/auth";
import type {
  PusherConversationUnreadEvent,
  PusherChatUnreadV2Event,
  PusherChatMentionEvent,
  ChannelsResponse,
} from "@/features/chat/types";
import { showChatBrowserNotification } from "@/features/chat/utils/chatNotifications";
import { getUserChatId } from "@/features/chat/utils/getUserChatId";
import { NOTIFICATIONS_QUERY_KEY } from "@/features/notifications";
import type { NotificationsResponse } from "@/features/notifications/types/notifications.type";
import { usePush } from "@/shared/hooks/usePush";
import { notifyTabAttention } from "@/shared/utils/tabAttention";

const formatAuthorName = (author: PusherChatUnreadV2Event["author"]) =>
  `${author.first_name} ${author.last_name}`.trim() || author.initials;

export const useGlobalUserChannel = (): null => {
  const { subscribe, unsubscribe } = usePush();
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const queryClientRef = useRef(queryClient);

  useEffect(() => {
    queryClientRef.current = queryClient;
  }, [queryClient]);

  // Chat v2: subscribe to private notification channel for real-time
  // unread updates and mentions. Browser notification + tab attention
  // use the rich payload (author + preview) when available.
  useEffect(() => {
    if (!isAuthenticated || !user?.employee?.id) return;

    const channelName = `private-employee_notifications_${user.employee.id}`;

    const chatUnreadHandler = (data?: PusherChatUnreadV2Event) => {
      queryClientRef.current.invalidateQueries({ queryKey: ["channels"] });
      notifyTabAttention();

      if (data?.author && data.message_preview) {
        const authorName = formatAuthorName(data.author);
        showChatBrowserNotification({
          title: data.chat_room_name || authorName,
          body: `${authorName}: ${data.message_preview}`,
          tag: `chat-${data.chat_room_id}`,
        });
      }
      else {
        showChatBrowserNotification();
      }
    };

    const chatMentionHandler = (data?: PusherChatMentionEvent) => {
      queryClientRef.current.invalidateQueries({ queryKey: ["channels"] });
      notifyTabAttention();

      if (data?.author) {
        const authorName = formatAuthorName(data.author);
        showChatBrowserNotification({
          title: `${authorName} vous a mentionné`,
          body: data.message_preview
            ? `${data.chat_room_name} — ${data.message_preview}`
            : data.chat_room_name,
          tag: `chat-mention-${data.chat_room_id}`,
        });
      }
    };

    subscribe<PusherChatUnreadV2Event>(channelName, "chat_unread", chatUnreadHandler);
    subscribe<PusherChatMentionEvent>(channelName, "chat_mention", chatMentionHandler);

    return () => {
      unsubscribe(channelName);
    };
  }, [user?.employee?.id, isAuthenticated, subscribe, unsubscribe]);

  // Legacy: subscribe to utilisateur channel for unread counts. When the
  // user also has a v2 employee.id, v2 owns browser-notification + tab
  // attention (richer payload) — legacy still updates the cache for the
  // navbar bell badge, but stays silent on the UI side to avoid duplicates.
  const v2Active = Boolean(user?.employee?.id);

  useEffect(() => {
    if (!isAuthenticated || !user?.employee?.key_chat) {
      return;
    }

    const userId = getUserChatId(user.employee.key_chat);

    if (!userId) {
      return;
    }

    const channelName = `utilisateur-${userId}`;

    const unreadHandler = async (data: PusherConversationUnreadEvent) => {
      const currentChannelsData = queryClientRef.current.getQueryData<ChannelsResponse>(["channels"]);

      const incomingConversationIds = data.content.map((item) => item.conversation_id);

      if (!currentChannelsData) {
        await queryClientRef.current.invalidateQueries({ queryKey: ["channels"] });
        if (!v2Active) {
          notifyTabAttention();
          showChatBrowserNotification();
        }
        return;
      }

      const unreadMap = new Map(
        data.content.map((item) => [item.conversation_id, item.number_of_unreads]),
      );

      const currentChannelIds = currentChannelsData.channels.map((channel) => channel.id);

      const activeConversationId = queryClientRef.current.getQueryData<number>(["activeConversationId"]);

      // Fire notification only when at least one conversation's unread count
      // *increased* — guards against echoing read receipts as alerts.
      const hasNewUnread = data.content.some((item) => {
        if (item.conversation_id === activeConversationId) return false;
        const prev = currentChannelsData.channels.find((c) => c.id === item.conversation_id);
        const prevCount = prev?.unread_messages_count ?? 0;
        return item.number_of_unreads > prevCount;
      });

      queryClientRef.current.setQueryData<ChannelsResponse>(["channels"], (old) => {
        if (!old) return old;

        const updatedChannels = old.channels.map((channel) => {
          const newUnreadCount = unreadMap.get(channel.id);
          if (newUnreadCount !== undefined) {
            if (channel.id === activeConversationId && newUnreadCount > 0) {
              return channel;
            }
            return { ...channel, unread_messages_count: newUnreadCount };
          }
          return channel;
        });

        return {
          ...old,
          channels: updatedChannels,
        };
      });

      const updatedChannelsData = queryClientRef.current.getQueryData<ChannelsResponse>(["channels"]);
      if (updatedChannelsData) {
        const conversationsWithUnreads = updatedChannelsData.channels.filter(
          (channel) => channel.unread_messages_count > 0,
        ).length;

        queryClientRef.current.setQueryData<NotificationsResponse>(
          NOTIFICATIONS_QUERY_KEY,
          (old) => {
            if (!old) {
              return { channels: { count: conversationsWithUnreads } };
            }

            return { ...old, channels: { count: conversationsWithUnreads } };
          },
        );
      }

      const hasNewConversations = incomingConversationIds.some(
        (conversationId) => !currentChannelIds.includes(conversationId),
      );

      if (hasNewConversations) {
        await queryClientRef.current.invalidateQueries({ queryKey: ["channels"] });
      }

      if (!v2Active && (hasNewUnread || hasNewConversations)) {
        notifyTabAttention();
        showChatBrowserNotification();
      }
    };
    subscribe<PusherConversationUnreadEvent>(channelName, "conversation-unread", unreadHandler);

    return () => {
      unsubscribe(channelName);
    };
  }, [user?.employee?.key_chat, v2Active, isAuthenticated, subscribe, unsubscribe]);

  return null;
};
